import {
  createTableCell,
  Column,
  Renderer,
  CellProps,
  convertISOStringToLocalDateString,
  Icon,
  Tooltip,
} from '@gcl/react-core';
import { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import '@common/Common.scss';
import { IResponsibleUser } from '@masterSchedule/detail/masterScheduleDetail.models';

type CellRendererProps<V, D> = { value: V; item: D; index: number; cellExtras: { [key: string]: any } };

export const createTableUserRoleColumn = <D extends object>(
  accessor: string,
  header: string | (() => ReactNode),
  disableResizing = false,
  disableSortBy = false,
  className:
    | string
    | ((cellRendererProps: CellRendererProps<string | IResponsibleUser[], D>) => string | undefined)
    | undefined = undefined,
) => {
  return createTableColumn<string | IResponsibleUser[], D>({
    accessor,
    header,
    disableResizing,
    disableSortBy,
    cellRenderer: createTableCell<string | IResponsibleUser[], D>((value, item, index, cellExtras) => {
      if (!value) return <></>;
      // let className = 'gcl-gems-master-schedule-list-t1-ref-no';
      // if (item.isCancelled) className = 'gcl-gems-master-schedule-list-t1-ref-no-cancelled';
      let classNameProp = '';
      if (className && typeof className === 'function') {
        const classTemp = className({ value, item, index, cellExtras });
        classNameProp = classTemp ?? '';
      } else if (className && typeof className === 'string') {
        classNameProp = className;
      }

      let content = '';
      if (typeof value === 'string') {
        content = value;
      } else if (typeof value === 'object' && value.length > 0) {
        content = value
          .map((t) => {
            return t.displayName;
          })
          .join(', ');
      }
      return (
        <Tooltip overlay={content}>
          <div className={`${classNameProp} gcl-gems-list-column-user-role`}>{content}</div>
        </Tooltip>
      );
    }),
  });

  // return createTableColumn<string, D>({
  //   accessor,
  //   header,
  //   disableResizing,
  //   disableSortBy,
  //   cellRenderer: createTableCell<string, D>((value, item, index, cellExtras) => {
  //     return <div>{value}</div>;
  //   }),
  // });
};
export const createTableDefaultColumn = <D extends object>(
  accessor: string,
  header: string | (() => ReactNode),
  onClick?: ({ value, item, index, cellExtras }: CellRendererProps<string, D>) => void,
  className?: (string | undefined) | ((cellRendererProps: CellRendererProps<string, D>) => string | undefined),
  style?: CSSProperties | ((cellRendererProps: CellRendererProps<string, D>) => CSSProperties),
  disableResizing = false,
  disableSortBy = false,
) => {
  return createTableColumn<string, D>({
    accessor,
    header,
    disableResizing,
    disableSortBy,
    onClick,
    className,
    style,
  });
};

export const createTableDateTimeColumn = <D extends object>(
  accessor: string,
  header: string | (() => ReactNode),
  customFormat: string | undefined = undefined,
  className: string | undefined = undefined,
  disableResizing = false,
  disableSortBy = false,
) => {
  return createTableColumn<string, D>({
    accessor,
    header,
    disableResizing,
    disableSortBy,
    cellRenderer: createTableCell<string, D>((value, item, index, cellExtras) => {
      return <div>{value ? convertISOStringToLocalDateString(value, customFormat) : undefined}</div>;
    }),
    className,
  });
};

export const createTableLinkWithOpenNewTabElement = ({
  linkUrl,
  linkDisplay,
}: {
  linkUrl: string;
  linkDisplay: string;
}) => {
  return (
    <>
      <Link to={linkUrl} className="gcl-mr-xs">
        {linkDisplay}
      </Link>
      <Link to={linkUrl} target="_blank" rel="noreferrer">
        <Icon iconName="BoxArrowUpRight" />
      </Link>
    </>
  );
};

export const createTableLinkWithOpenNewTabColumn = <D extends object>(
  accessor: string,
  header: string | (() => ReactNode),
  link: (cellRendererProps: CellRendererProps<string, D>) => string,
  render: (cellRendererProps: CellRendererProps<string, D>, link: ReactNode) => ReactNode,
  disableResizing = false,
  disableSortBy = false,
) => {
  return createTableColumn<string, D>({
    accessor,
    header,
    disableResizing,
    disableSortBy,
    cellRenderer: createTableCell<string, D>((value, item, index, cellExtras) => {
      const linkUrl = link({ value, item, index, cellExtras });
      if (linkUrl.length <= 0) {
        return render({ value, item, index, cellExtras }, <div>{value}</div>);
      }
      const linkElement = createTableLinkWithOpenNewTabElement({ linkUrl, linkDisplay: value });
      const view = render({ value, item, index, cellExtras }, linkElement);
      return view;
    }),
  });
};

export const createTableLinkColumn = <D extends object>(
  accessor: string,
  header: string | (() => ReactNode),
  handleItem: (item: object) => { onClick: (() => void) | undefined; displayText: string | undefined },
  disableSortBy = false,
) => {
  return createTableColumn<string, D>({
    accessor,
    header,
    disableResizing: false,
    disableSortBy,
    cellRenderer: createTableCell<string, D>(
      (value: string, item: object, index: number, cellExtras: { [key: string]: any }) => {
        const { onClick, displayText } = handleItem(item);
        if (!onClick || !displayText) return <span />;

        return (
          <div onClick={onClick} className="gcl-fl-md ">
            {displayText}
          </div>
        );
      },
    ),
  });
};

export interface ICreateTableColumnProps<V, D extends object> {
  accessor: string;
  header: string | (() => ReactNode);
  cellRenderer?: Renderer<CellProps<D, V>>;
  disableResizing?: boolean;
  disableSortBy?: boolean;
  onClick?: ({ value, item, index, cellExtras }: CellRendererProps<V, D>) => void;
  className?: (string | undefined) | ((cellRendererProps: CellRendererProps<V, D>) => string | undefined);
  style?: CSSProperties | ((cellRendererProps: CellRendererProps<V, D>) => CSSProperties);
}

export const createTableColumn = <V, D extends object>({
  accessor,
  header,
  cellRenderer,
  disableResizing = false,
  disableSortBy = false,
  onClick,
  className,
  style,
}: ICreateTableColumnProps<V, D>) => {
  return {
    accessor,
    Header: header,
    disableSortBy,
    disableResizing,
    width: undefined,
    maxWidth: undefined,
    minWidth: undefined,
    Cell:
      cellRenderer ||
      createTableCell<V, D>((value, item, index, cellExtras) => {
        const onClickProp = onClick
          ? {
              onClick: () => {
                onClick({ value, item, index, cellExtras });
              },
            }
          : {};
        let classNameProp = {};
        if (className && typeof className === 'function') {
          const classTemp = className({ value, item, index, cellExtras });
          classNameProp = classTemp
            ? {
                className: classTemp,
              }
            : {};
        } else if (className && typeof className === 'string') {
          classNameProp = { className };
        }
        let styleProp = {};
        if (style && typeof style === 'function') {
          styleProp = {
            style: style({ value, item, index, cellExtras }),
          };
        } else if (style && typeof style === 'object') {
          styleProp = { style };
        }
        return (
          <div {...onClickProp} {...classNameProp} {...styleProp}>
            {value?.toString() || ' '}
          </div>
        );
      }),
  } as Column;
};
